/**
 * Images that survive no service (Sam, 2026-10-04: the Events page came back from an elevator with its titles but
 * no pictures). The query cache keeps the rows, but every picture was a plain <img> on a CDN that sends no cache
 * headers (s1.ticketm.net: an ETag and nothing else), so WebKit has to revalidate it and cannot while offline.
 *
 * This keeps the bytes ourselves, in IndexedDB - the one store on capacitor://localhost that is big enough (the
 * query cache's Preferences/localStorage budget is ~5 MB; a service worker needs app-bound domains and could not
 * cover every image host anyway). Rule: an image the app has shown once is served from here afterwards, online or
 * not. Nothing is fetched ahead of being shown except what the festival download warms.
 *
 *   useCachedImageSrc(url, { eager })  - the src for an <img>: the stored copy when there is one, else the network
 *   getCachedImageSrc(url)             - same for code that loads images itself (the map's avatar textures)
 *   warmImageCache(urls)               - store these now (festival download: the event art, the lineup's faces)
 *
 * Object URLs are kept for the session (one per distinct image) rather than revoked, so a card that remounts
 * does not re-read the blob. Bounded by MAX_ENTRIES / MAX_BYTES, oldest-used first.
 */

const DB_NAME = 'prii-images';
const STORE = 'images';
/** Bookkeeping ({url, at, bytes}) apart from the blobs, so touching and pruning never rewrite or read a picture. */
const META = 'meta';
const DB_VERSION = 2;
const TOUCH_EVERY_MS = 60 * 60 * 1000;
const MAX_ENTRIES = 600;
const MAX_BYTES = 150 * 1024 * 1024;
const PRUNE_EVERY = 20;

const objectUrls = new Map(); // url -> blob: URL for this session
const inflight = new Map(); // url -> Promise<string | null>
let dbPromise = null;
let writesSincePrune = 0;

function cacheable(url) {
  return typeof url === 'string' && /^https?:\/\//.test(url);
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'url' });
        if (!db.objectStoreNames.contains(META))
          db.createObjectStore(META, { keyPath: 'url' }).createIndex('at', 'at');
      };
      req.onsuccess = () => {
        const db = req.result;
        // WebKit closes the connection after a long background or under memory pressure; a memoised dead
        // connection would make every read fail until relaunch (2026-10-04 audit). Forget it and reopen next time.
        db.onclose = () => {
          if (dbPromise) dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function tx(db, mode, run, stores = [STORE]) {
  return new Promise((resolve) => {
    try {
      const t = db.transaction(stores, mode);
      const store = t.objectStore(stores[0]);
      let out;
      run(
        store,
        (v) => {
          out = v;
        },
        t,
      );
      t.oncomplete = () => resolve(out);
      t.onerror = () => resolve(undefined);
      t.onabort = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

async function readRecord(url) {
  const db = await openDb();
  if (!db) return null;
  const rec = await tx(db, 'readonly', (store, set) => {
    const r = store.get(url);
    r.onsuccess = () => set(r.result ?? null);
  });
  return rec ?? null;
}

async function writeRecord(rec) {
  const db = await openDb();
  if (!db) return false;
  const ok = await tx(
    db,
    'readwrite',
    (store, set, t) => {
      store.put({ url: rec.url, blob: rec.blob });
      t.objectStore(META).put({ url: rec.url, at: rec.at, bytes: rec.bytes });
      set(true);
    },
    [STORE, META],
  );
  if (ok && ++writesSincePrune >= PRUNE_EVERY) {
    writesSincePrune = 0;
    void prune(db);
  }
  return Boolean(ok);
}

const touchedAt = new Map(); // url -> last touch this session
async function touch(url) {
  const last = touchedAt.get(url) ?? 0;
  if (Date.now() - last < TOUCH_EVERY_MS) return;
  touchedAt.set(url, Date.now());
  const db = await openDb();
  if (!db) return;
  await tx(
    db,
    'readwrite',
    (store) => {
      const r = store.get(url);
      r.onsuccess = () => {
        if (r.result) store.put({ ...r.result, at: Date.now() });
      };
    },
    [META],
  );
}

/** Oldest-used first until under both caps - from the bookkeeping store, never touching a blob. */
async function prune(db) {
  const rows = await tx(
    db,
    'readonly',
    (store, set) => {
      const out = [];
      const r = store.index('at').openCursor();
      r.onsuccess = () => {
        const c = r.result;
        if (c) {
          out.push({ url: c.value.url, bytes: c.value.bytes || 0 });
          c.continue();
        } else set(out);
      };
    },
    [META],
  );
  if (!rows) return;
  let total = rows.reduce((a, r) => a + r.bytes, 0);
  let count = rows.length;
  const drop = [];
  for (const r of rows) {
    if (count <= MAX_ENTRIES && total <= MAX_BYTES) break;
    drop.push(r.url);
    count -= 1;
    total -= r.bytes;
  }
  if (!drop.length) return;
  await tx(
    db,
    'readwrite',
    (store, _set, t) => {
      const meta = t.objectStore(META);
      for (const u of drop) {
        store.delete(u);
        meta.delete(u);
      }
    },
    [STORE, META],
  );
  for (const u of drop) {
    const o = objectUrls.get(u);
    if (o) {
      URL.revokeObjectURL(o);
      objectUrls.delete(u);
    }
  }
}

function objectUrlFor(url, blob) {
  const have = objectUrls.get(url);
  if (have) return have;
  const o = URL.createObjectURL(blob);
  objectUrls.set(url, o);
  return o;
}

/** A copy already opened this session, synchronously; null otherwise (the store itself is async). */
export function getSessionImageSrc(url) {
  return (cacheable(url) && objectUrls.get(url)) || null;
}

/** The stored copy as a blob: URL, or null. */
export async function getStoredImageSrc(url) {
  if (!cacheable(url)) return null;
  const have = objectUrls.get(url);
  if (have) return have;
  const rec = await readRecord(url);
  if (!rec?.blob) return null;
  void touch(url);
  return objectUrlFor(url, rec.blob);
}

/** Fetch and store; resolves the blob: URL, or null when the network or CORS said no (the <img> keeps the plain URL). */
export function cacheImage(url) {
  if (!cacheable(url)) return Promise.resolve(null);
  const have = objectUrls.get(url);
  if (have) return Promise.resolve(have);
  const running = inflight.get(url);
  if (running) return running;
  const p = (async () => {
    try {
      // Already stored (another mount got there first, or the <img> loaded before the store answered): no fetch.
      const stored = await getStoredImageSrc(url);
      if (stored) return stored;
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!blob.size || !/^image\//.test(blob.type || 'image/')) return null;
      await writeRecord({ url, blob, bytes: blob.size, at: Date.now() });
      return objectUrlFor(url, blob);
    } catch {
      return null;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

/** For code that loads images itself: stored copy, else fetch-and-store, else the plain URL. */
export async function getCachedImageSrc(url) {
  if (!cacheable(url)) return url;
  return (await getStoredImageSrc(url)) ?? (await cacheImage(url)) ?? url;
}

/** Store these now if they are not already (the festival download). Never throws, never blocks. */
export function warmImageCache(urls) {
  const list = [...new Set((urls || []).filter(cacheable))];
  (async () => {
    for (const url of list) {
      if (await getStoredImageSrc(url)) continue;
      await cacheImage(url);
    }
  })().catch(() => {});
}
